import { Component, computed, inject, Input, signal } from '@angular/core';
import { InspectionPdfMergeDto, InspectionPdfService } from '@artemis-software/wr-api';
import { firstValueFrom, throwError, timeout } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { loader } from '@/utils/signals/loader';
import { InspectionContext } from '@pages/inspection-detail-page-v2/inspection.context';
import InspectionPdfTypeEnum = InspectionPdfMergeDto.InspectionPdfTypeEnum;

@Component({
  standalone: false,
  selector: 'wr-pdf-share-link-dialog',
  templateUrl: './pdf-share-link-dialog.component.html',
  styleUrls: ['./pdf-share-link-dialog.component.scss'],
})
export class PdfShareLinkDialogComponent {
  private readonly inspectionPdfService = inject(InspectionPdfService);
  private readonly inspectionContext = inject(InspectionContext);

  private readonly _inspectionPdfType = signal<InspectionPdfTypeEnum | undefined>(undefined);

  @Input() set inspectionPdfType(value: InspectionPdfTypeEnum | undefined) {
    this._inspectionPdfType.set(value);
  }

  get inspectionPdfType() {
    return this._inspectionPdfType();
  }

  readonly inspectionPdf = computed(() =>
    this.inspectionContext.inspectionPdfs().find(inspectionPdf =>
      inspectionPdf.inspectionPdfType == this.inspectionPdfType));

  readonly loading = loader();
  durations: number[] = [1, 7, 30, 90, 365];
  selectedDuration = signal(1);

  readonly shareLink = computed(() => {
    const pdf = this.inspectionPdf();
    if (pdf?.pdfShareLink) {
      return `${window.location.origin}/pdf/${pdf.pdfShareLink.id}`;
    }
    return '';
  });

  async generateShareLink() {
    const pdf = this.inspectionPdf();
    if (!pdf) return;

    const loader = this.loading.add();
    loader.set(true);

    try {
      await firstValueFrom(
        this.inspectionPdfService.generateShareLink({
          inspectionPdfId: pdf.id,
          durationDays: this.selectedDuration(),
        }).pipe(
          timeout(15000),
          catchError(error => {
            console.error(error.name === 'TimeoutError' ? 'Timeout' : error.message);
            return throwError(() => error);
          })
        )
      );
      this.inspectionContext.reloadInspectionPdfs();
    } finally {
      loader.set(false);
      loader.stop();
    }
  }

  copyToClipboard(link: string) {
    navigator.clipboard.writeText(link).catch(err => console.error(err));
  }

  openLink(link: string) {
    if (link) window.open(link, '_blank');
  }
}
